import Phaser from "phaser";
import type { Power } from "../systems/score";
import {
  applyAirAccel,
  applyGroundAccel,
  COYOTE_MS,
  GRAVITY_FALL,
  GRAVITY_HELD,
  JUMP_BUFFER_MS,
  JUMP_CUT,
  jumpVelocity,
  WALK_MAX,
} from "../systems/platformPhysics";

export class Player extends Phaser.Physics.Arcade.Sprite {
  readonly head: Phaser.GameObjects.Image;
  power: Power = "small";
  facing = 1;
  grounded = false;
  jumping = false;
  jumpCutUsed = false;
  skidding = false;
  ducking = false;
  dead = false;
  starUntil = 0;
  invulnUntil = 0;
  frozen = false;
  walkTimer = 0;
  fire = false;
  justJumped = false;
  justLanded = false;
  private coyoteUntil = 0;
  private jumpBufferedUntil = 0;
  private wasGrounded = false;

  constructor(scene: Phaser.Scene, x: number, y: number, power: Power) {
    super(scene, x, y, power === "small" ? "player-small-idle" : "player-super-idle");
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setOrigin(0.5, 1);
    this.setDepth(20);
    this.head = scene.add
      .image(x, y - 35, "portrait-part")
      .setDisplaySize(26, 26)
      .setDepth(22);
    this.setPower(power, true);
  }

  get arcade(): Phaser.Physics.Arcade.Body {
    return this.body as Phaser.Physics.Arcade.Body;
  }

  get isStar(): boolean {
    return this.scene.time.now < this.starUntil;
  }

  get isInvulnerable(): boolean {
    return this.scene.time.now < this.invulnUntil || this.isStar;
  }

  get isBig(): boolean {
    return this.power !== "small";
  }

  setPower(power: Power, instant = false): void {
    const bottom = this.y;
    this.power = power;
    this.fire = power === "fire";
    this.applyBody();
    this.y = bottom;
    this.refreshAnim(true);
    if (!instant) {
      this.invulnUntil = this.scene.time.now + 400;
    }
  }

  applyBody(): void {
    const body = this.arcade;
    if (this.power === "small") {
      this.setSize(18, 24);
      this.setOffset(15, 24);
    } else if (this.ducking) {
      this.setSize(24, 24);
      this.setOffset(12, 40);
    } else {
      this.setSize(22, 42);
      this.setOffset(13, 22);
    }
    body.setMaxVelocity(330, 720);
    body.setAllowGravity(true);
  }

  kill(): void {
    if (this.dead) return;
    this.dead = true;
    this.frozen = true;
    this.arcade.setVelocity(0, -280);
    this.arcade.checkCollision.none = true;
    this.arcade.setAllowGravity(true);
    this.arcade.setGravityY(GRAVITY_FALL);
    this.setTexture("player-small-dead");
    this.head.setAngle(-12);
  }

  hurtOrKill(): "hurt" | "kill" | "ignore" {
    if (this.dead || this.isInvulnerable) return "ignore";
    if (this.isBig) {
      this.setPower("small");
      this.invulnUntil = this.scene.time.now + 1800;
      return "hurt";
    }
    this.kill();
    return "kill";
  }

  grantStar(duration = 8000): void {
    this.starUntil = this.scene.time.now + duration;
  }

  control(
    dt: number,
    input: { left: boolean; right: boolean; jump: boolean; jumpPressed: boolean; down: boolean; run: boolean },
  ): void {
    if (this.dead || this.frozen) return;
    const body = this.arcade;
    this.justJumped = false;
    this.justLanded = false;
    this.grounded =
      body.blocked.down ||
      body.touching.down ||
      (body.wasTouching.down && body.velocity.y >= 0);
    this.justLanded = this.grounded && !this.wasGrounded && body.velocity.y >= 0;
    this.wasGrounded = this.grounded;
    if (this.grounded) this.coyoteUntil = this.scene.time.now + COYOTE_MS;
    if (input.jumpPressed) {
      this.jumpBufferedUntil = this.scene.time.now + JUMP_BUFFER_MS;
    }
    this.ducking = input.down && this.isBig && this.grounded;
    this.applyBody();

    const dir = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    if (dir !== 0 && !this.ducking) this.facing = dir;

    if (this.ducking && this.grounded) {
      const slowed = applyGroundAccel(body.velocity.x, 0, false, dt);
      body.setVelocityX(slowed.vx);
      this.skidding = false;
    } else if (this.grounded) {
      const next = applyGroundAccel(body.velocity.x, dir, input.run, dt);
      body.setVelocityX(next.vx);
      this.skidding = next.skidding;
    } else {
      body.setVelocityX(applyAirAccel(body.velocity.x, dir, input.run, dt));
      this.skidding = false;
    }

    if (
      this.jumpBufferedUntil >= this.scene.time.now &&
      (this.grounded || this.coyoteUntil >= this.scene.time.now) &&
      !this.ducking
    ) {
      body.setVelocityY(jumpVelocity(body.velocity.x));
      this.jumping = true;
      this.jumpCutUsed = false;
      this.grounded = false;
      this.wasGrounded = false;
      this.jumpBufferedUntil = 0;
      this.coyoteUntil = 0;
      this.justJumped = true;
    }

    if (!input.jump) {
      this.jumping = false;
      if (!this.jumpCutUsed && body.velocity.y < 0) {
        body.setVelocityY(body.velocity.y * JUMP_CUT);
        this.jumpCutUsed = true;
      }
    }

    const held = this.jumping && input.jump && body.velocity.y < 0;
    body.setGravityY(held ? GRAVITY_HELD : GRAVITY_FALL);

    if (this.grounded && body.velocity.y > 0) {
      body.setVelocityY(0);
    }

    this.walkTimer += dt;
    this.refreshAnim(false);
    this.updateFlash();
  }

  preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    this.syncHead();
  }

  bounce(velocity: number): void {
    this.arcade.setVelocityY(velocity);
    this.grounded = false;
    this.jumping = true;
    this.jumpCutUsed = false;
  }

  private updateFlash(): void {
    if (this.isStar) {
      const flash = Math.floor(this.scene.time.now / 80) % 3;
      this.setTint(flash === 0 ? 0xffffff : flash === 1 ? 0x88ff88 : 0xff88ff);
      this.head.setTint(
        flash === 0 ? 0xffffff : flash === 1 ? 0x88ff88 : 0xff88ff,
      );
      this.setAlpha(1);
      this.head.setAlpha(1);
      return;
    }
    this.clearTint();
    this.head.clearTint();
    if (this.scene.time.now < this.invulnUntil) {
      const alpha = Math.floor(this.scene.time.now / 80) % 2 === 0 ? 1 : 0.35;
      this.setAlpha(alpha);
      this.head.setAlpha(alpha);
    } else {
      this.setAlpha(1);
      this.head.setAlpha(1);
    }
  }

  refreshAnim(force: boolean): void {
    if (this.dead) return;
    const prefix = this.power === "small" ? "player-small-" : "player-super-";
    const fire = this.fire ? "fire-" : "";
    let pose: string = "idle";
    if (this.ducking && this.isBig) pose = "duck";
    else if (!this.grounded) pose = "jump";
    else if (this.skidding) pose = "skid";
    else if (Math.abs(this.arcade.velocity.x) > 18) {
      const cycle = Math.floor(this.walkTimer * 12) % 3;
      if (Math.abs(this.arcade.velocity.x) > WALK_MAX * 1.1) {
        pose = cycle === 1 ? "walk2" : "run";
      } else {
        pose = cycle === 0 ? "idle" : cycle === 1 ? "walk" : "walk2";
      }
    }
    const key = `${prefix}${fire}${pose}`;
    if (force || this.texture.key !== key) {
      if (this.scene.textures.exists(key)) this.setTexture(key);
    }
    this.setFlipX(this.facing < 0);
  }

  private syncHead(): void {
    if (!this.head.active) return;
    const speedBob =
      this.grounded && Math.abs(this.arcade.velocity.x) > 20
        ? Math.sin(this.walkTimer * 18) * 1.2
        : 0;
    const size = this.power === "small" ? 26 : 32;
    const duckOffset = this.ducking ? 10 : 0;
    this.head
      .setPosition(
        this.x + this.facing * (this.ducking ? 1 : 0),
        // Head bottom overlaps the torso top by a few pixels in both sizes.
        this.y - (this.power === "small" ? 33 : 44) + speedBob + duckOffset,
      )
      .setDisplaySize(size, size)
      .setFlipX(this.facing < 0);
  }

  destroy(fromScene?: boolean): void {
    this.head.destroy(fromScene);
    super.destroy(fromScene);
  }
}
